/* ==========================================================================
   ALVERRA X — Global JavaScript
   Vanilla, dependency-free custom elements. No jQuery. No libraries.
   ========================================================================== */
(() => {
  'use strict';

  /* ---------------------------------------------------------------- utils */
  const debounce = (fn, wait = 200) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };

  const fetchConfig = (type = 'json') => ({
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` }
  });

  const formatMoney = (cents) => {
    // Strip any HTML from the store's money format (e.g. "<span class=money>...")
    // because prices are injected with textContent, not innerHTML.
    const format = (window.AlverraX?.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
    const amount = (cents / 100).toFixed(2);
    const [whole, decimal] = amount.split('.');
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace(/\{\{\s*amount\s*\}\}/, `${withCommas}.${decimal}`)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${decimal}`);
  };

  const trapFocus = (container) => {
    const focusable = container.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])');
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    container.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    setTimeout(() => first.focus(), 60);
  };

  const showToast = (message) => {
    let toast = document.querySelector('.toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'toast';
      toast.setAttribute('role', 'status');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => toast.classList.remove('is-visible'), 3200);
  };

  window.AlverraXUtils = { debounce, formatMoney, showToast };

  /* --------------------------------------------------------- scroll reveal */
  const initReveal = () => {
    if (!document.body.classList.contains('reveal-enabled')) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    document.querySelectorAll('.reveal:not(.is-revealed)').forEach((el) => io.observe(el));
    document.addEventListener('shopify:section:load', () => {
      document.querySelectorAll('.reveal:not(.is-revealed)').forEach((el) => io.observe(el));
    });
  };

  /* ---------------------------------------------------- marquee normalizer */
  // Same visual speed for every marquee/ticker regardless of content length:
  // the --marquee-speed setting means "seconds per 1000px of content".
  const initMarquees = () => {
    const normalize = () => {
      document.querySelectorAll('.marquee__track').forEach((track) => {
        const base = parseFloat(getComputedStyle(track.parentElement).getPropertyValue('--marquee-speed')) || 30;
        const half = track.scrollWidth / 2;
        if (half > 0) track.style.animationDuration = `${((base * half) / 1000).toFixed(2)}s`;
      });
    };
    normalize();
    window.addEventListener('load', normalize);
    window.addEventListener('resize', debounce(normalize, 250));
    document.addEventListener('shopify:section:load', normalize);
  };

  /* -------------------------------------------------------- sticky header */
  const initHeader = () => {
    const wrapper = document.querySelector('.header-wrapper');
    if (!wrapper) return;
    const onScroll = () => wrapper.classList.toggle('is-scrolled', window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  };

  /* ------------------------------------------------------- drawer element */
  class DrawerElement extends HTMLElement {
    connectedCallback() {
      this.overlay = this.querySelector('.drawer__overlay');
      this.overlay?.addEventListener('click', () => this.close());
      this.querySelectorAll('[data-drawer-close]').forEach((btn) => btn.addEventListener('click', () => this.close()));
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.classList.contains('is-open')) this.close(); });
    }
    open(trigger) {
      this.trigger = trigger || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('drawer-open');
      const panel = this.querySelector('.drawer__panel');
      if (panel) trapFocus(panel);
    }
    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      if (!document.querySelector('drawer-element.is-open')) document.body.classList.remove('drawer-open');
      this.trigger?.focus?.();
    }
  }
  customElements.define('drawer-element', DrawerElement);

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (!opener) return;
    const drawer = document.getElementById(opener.dataset.drawerOpen);
    if (drawer?.open) { e.preventDefault(); drawer.open(opener); }
  });

  /* ------------------------------------------------------------ mobile nav */
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-subnav-toggle]');
    if (!toggle) return;
    const sub = toggle.nextElementSibling;
    if (!sub) return;
    const isOpen = sub.classList.toggle('is-open');
    toggle.setAttribute('aria-expanded', isOpen);
    const icon = toggle.querySelector('[data-caret]');
    if (icon) icon.style.transform = isOpen ? 'rotate(180deg)' : '';
  });

  /* -------------------------------------------------------- quantity input */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelector('[name="minus"]')?.addEventListener('click', () => this.step(-1));
      this.querySelector('[name="plus"]')?.addEventListener('click', () => this.step(1));
    }
    step(dir) {
      const value = Math.max(parseInt(this.input.min || 1, 10), (parseInt(this.input.value, 10) || 1) + dir);
      const max = parseInt(this.input.max, 10);
      this.input.value = max && value > max ? max : value;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ----------------------------------------------------------------- cart */
  const Cart = {
    async add(body) {
      const res = await fetch(`${window.AlverraX.routes.cart_add_url}.js`, { ...fetchConfig(), body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw data;
      await Cart.refresh(true);
      return data;
    },
    async change(line, quantity) {
      const res = await fetch(`${window.AlverraX.routes.cart_change_url}.js`, { ...fetchConfig(), body: JSON.stringify({ line, quantity }) });
      const data = await res.json();
      if (!res.ok) throw data;
      await Cart.refresh(false);
      return data;
    },
    async refresh(open) {
      const res = await fetch(`${window.location.pathname}?sections=cart-drawer`);
      const sections = await res.json();
      const html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');

      const drawerBody = document.querySelector('#CartDrawer [data-cart-drawer-body]');
      const newBody = html.querySelector('[data-cart-drawer-body]');
      if (drawerBody && newBody) drawerBody.innerHTML = newBody.innerHTML;

      const drawerFooter = document.querySelector('#CartDrawer [data-cart-drawer-footer]');
      const newFooter = html.querySelector('[data-cart-drawer-footer]');
      if (drawerFooter && newFooter) drawerFooter.innerHTML = newFooter.innerHTML;

      const newCount = html.querySelector('[data-cart-count-source]');
      document.querySelectorAll('.cart-count').forEach((el) => {
        const count = newCount ? newCount.dataset.cartCountSource : '0';
        el.textContent = count === '0' ? '' : count;
        el.dataset.count = count;
      });

      document.dispatchEvent(new CustomEvent('cart:updated'));

      if (open) {
        const drawer = document.getElementById('CartDrawer');
        if (drawer && window.AlverraX.cartType === 'drawer') drawer.open();
        else if (!drawer || window.AlverraX.cartType !== 'drawer') window.location.href = window.AlverraX.routes.cart_url;
      }
    }
  };
  window.AlverraXCart = Cart;

  /* Cart line quantity / remove (event delegation — survives re-render) */
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-line-quantity]');
    if (!input) return;
    Cart.change(parseInt(input.dataset.line, 10), parseInt(input.value, 10) || 0).catch((err) => showToast(err.description || 'Unable to update cart'));
  });
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-line-remove]');
    if (!btn) return;
    e.preventDefault();
    Cart.change(parseInt(btn.dataset.line, 10), 0).catch(() => showToast('Unable to update cart'));
  });
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-upsell-add]');
    if (!btn) return;
    e.preventDefault();
    btn.classList.add('is-loading');
    Cart.add({ items: [{ id: parseInt(btn.dataset.upsellAdd, 10), quantity: 1 }] })
      .then(() => showToast('Added to cart'))
      .catch((err) => showToast(err.description || 'Unable to add item'))
      .finally(() => btn.classList.remove('is-loading'));
  });

  /* ----------------------------------------------------------- product form */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.submitBtn = this.querySelector('[type="submit"]');
      this.form?.addEventListener('submit', (e) => this.onSubmit(e));
    }
    async onSubmit(e) {
      e.preventDefault();
      if (this.submitBtn?.getAttribute('aria-disabled') === 'true') return;
      this.submitBtn?.classList.add('is-loading');
      const formData = new FormData(this.form);
      const body = {
        items: [{
          id: parseInt(formData.get('id'), 10),
          quantity: parseInt(formData.get('quantity') || 1, 10)
        }]
      };
      const props = {};
      for (const [key, value] of formData.entries()) {
        const m = key.match(/^properties\[(.+)\]$/);
        if (m && value) props[m[1]] = value;
      }
      if (Object.keys(props).length) body.items[0].properties = props;
      try {
        await Cart.add(body);
        if (window.AlverraX.cartType !== 'drawer') return;
        showToast('Added to cart');
      } catch (err) {
        showToast(err.description || err.message || 'Unable to add to cart');
      } finally {
        this.submitBtn?.classList.remove('is-loading');
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* -------------------------------------------------------- variant picker */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector('[data-variants-json]');
      if (!dataEl) return;
      this.variants = JSON.parse(dataEl.textContent);
      this.sectionId = this.dataset.section;
      this.addEventListener('change', () => this.onChange());
    }
    get selectedOptions() {
      return Array.from(this.querySelectorAll('fieldset')).map(
        (fs) => fs.querySelector('input:checked')?.value
      ).concat(Array.from(this.querySelectorAll('select[data-option-select]')).map((s) => s.value)).filter(Boolean);
    }
    onChange() {
      const options = this.selectedOptions;
      const variant = this.variants.find((v) => v.options.every((opt, i) => opt === options[i]));
      this.updateLabels();
      const section = document.getElementById(`MainProduct-${this.sectionId}`) || document;

      const idInput = section.querySelector('form[data-type="add-to-cart-form"] input[name="id"], product-form input[name="id"]');
      const btn = section.querySelector('product-form [type="submit"]');
      const btnLabel = btn?.querySelector('.button-label');

      if (!variant) {
        if (btn) { btn.setAttribute('aria-disabled', 'true'); if (btnLabel) btnLabel.textContent = window.AlverraX.strings.unavailable; }
        return;
      }

      if (idInput) { idInput.value = variant.id; idInput.dispatchEvent(new Event('change', { bubbles: true })); }

      // Price
      const onSale = variant.compare_at_price > variant.price;
      section.querySelectorAll('[data-main-price]').forEach((el) => {
        el.textContent = formatMoney(variant.price);
        el.classList.toggle('price__sale', onSale);
      });
      section.querySelectorAll('[data-main-compare]').forEach((el) => {
        if (onSale) {
          el.textContent = formatMoney(variant.compare_at_price);
          el.removeAttribute('hidden');
        } else el.setAttribute('hidden', '');
      });

      // Availability
      if (btn) {
        if (variant.available) {
          btn.removeAttribute('aria-disabled');
          if (btnLabel) btnLabel.textContent = btn.dataset.addLabel || window.AlverraX.strings.addToCart;
        } else {
          btn.setAttribute('aria-disabled', 'true');
          if (btnLabel) btnLabel.textContent = window.AlverraX.strings.soldOut;
        }
      }

      // Media
      if (variant.featured_media?.id) {
        const gallery = section.querySelector('media-gallery');
        gallery?.showMedia(String(variant.featured_media.id));
      }

      // Sticky bar price
      section.querySelectorAll('[data-sticky-price]').forEach((el) => { el.textContent = formatMoney(variant.price); });

      // URL
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());

      document.dispatchEvent(new CustomEvent('variant:changed', { detail: { variant } }));
    }
    updateLabels() {
      this.querySelectorAll('fieldset').forEach((fs) => {
        const val = fs.querySelector('input:checked')?.value;
        const label = fs.querySelector('[data-selected-value]');
        if (label && val) label.textContent = val;
      });
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* --------------------------------------------------------- media gallery */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.product-media'));
      this.thumbs = Array.from(this.querySelectorAll('.product-gallery__thumb'));
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.showMedia(t.dataset.mediaId)));
      this.querySelector('.product-gallery__nav--prev')?.addEventListener('click', () => this.stepMedia(-1));
      this.querySelector('.product-gallery__nav--next')?.addEventListener('click', () => this.stepMedia(1));
      const canHover = window.matchMedia('(hover: hover)').matches;
      if (!canHover) return; // tap-to-zoom is confusing on touch devices
      this.querySelectorAll('.product-media--zoomable').forEach((m) => {
        m.addEventListener('click', (e) => {
          m.classList.toggle('is-zoomed');
          const img = m.querySelector('img');
          if (img && m.classList.contains('is-zoomed')) {
            const rect = m.getBoundingClientRect();
            const x = ((e.clientX - rect.left) / rect.width) * 100;
            const y = ((e.clientY - rect.top) / rect.height) * 100;
            img.style.transformOrigin = `${x}% ${y}%`;
          }
        });
      });
    }
    showMedia(id) {
      const target = this.items.find((i) => i.dataset.mediaId === String(id));
      if (!target) return;
      this.items.forEach((i) => i.classList.toggle('is-active', i === target));
      this.thumbs.forEach((t) => t.classList.toggle('is-active', t.dataset.mediaId === String(id)));
      const activeThumb = this.thumbs.find((t) => t.dataset.mediaId === String(id));
      if (activeThumb) {
        // Scroll only the thumbnail strip — scrollIntoView can pan the whole page.
        const strip = this.querySelector('.product-gallery__thumbs');
        if (strip) {
          strip.scrollTo({
            left: activeThumb.offsetLeft - strip.clientWidth / 2 + activeThumb.clientWidth / 2,
            behavior: 'smooth'
          });
        }
      }
      target.querySelector('video')?.play?.().catch(() => {});
    }
    stepMedia(dir) {
      const current = this.items.findIndex((i) => i.classList.contains('is-active'));
      const next = (current + dir + this.items.length) % this.items.length;
      this.showMedia(this.items[next].dataset.mediaId);
    }
  }
  customElements.define('media-gallery', MediaGallery);

  /* ------------------------------------------------------------ sticky ATC */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const target = document.querySelector('[data-main-buy-buttons]');
      if (!target) return;
      const io = new IntersectionObserver(([entry]) => {
        this.querySelector('.sticky-atc')?.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      }, { threshold: 0 });
      io.observe(target);
      this.querySelector('[data-sticky-submit]')?.addEventListener('click', () => {
        const form = document.querySelector('product-form form');
        form?.dispatchEvent(new Event('submit', { cancelable: true }));
      });
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* --------------------------------------------------------------- slider */
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.slider = this.querySelector('.slider');
      this.prev = this.querySelector('[data-slider-prev]');
      this.next = this.querySelector('[data-slider-next]');
      if (!this.slider) return;
      this.prev?.addEventListener('click', () => this.scrollByPage(-1));
      this.next?.addEventListener('click', () => this.scrollByPage(1));
      this.slider.addEventListener('scroll', debounce(() => this.updateButtons(), 80), { passive: true });
      this.updateButtons();
    }
    scrollByPage(dir) {
      this.slider.scrollBy({ left: dir * this.slider.clientWidth * 0.85, behavior: 'smooth' });
    }
    updateButtons() {
      if (!this.prev || !this.next) return;
      const max = this.slider.scrollWidth - this.slider.clientWidth - 4;
      this.prev.toggleAttribute('disabled', this.slider.scrollLeft <= 4);
      this.next.toggleAttribute('disabled', this.slider.scrollLeft >= max);
    }
  }
  customElements.define('slider-component', SliderComponent);

  /* ---------------------------------------------------- predictive search */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      if (!this.input || !this.results || !window.AlverraX.predictiveSearch) return;
      this.input.addEventListener('input', debounce(() => this.search(), 280));
    }
    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) { this.results.innerHTML = ''; return; }
      try {
        const url = `${window.AlverraX.routes.predictive_search_url}?q=${encodeURIComponent(q)}&resources[type]=product,collection,query&resources[limit]=6&section_id=predictive-search`;
        const res = await fetch(url);
        if (!res.ok) return;
        const text = await res.text();
        const html = new DOMParser().parseFromString(text, 'text/html');
        const inner = html.querySelector('#PredictiveSearchResults');
        this.results.innerHTML = inner ? inner.innerHTML : '';
      } catch (e) { /* silent */ }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ------------------------------------------------------------- countdown */
  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      const end = new Date(this.dataset.endsAt).getTime();
      if (Number.isNaN(end)) return;
      this.units = {
        days: this.querySelector('[data-days]'),
        hours: this.querySelector('[data-hours]'),
        minutes: this.querySelector('[data-minutes]'),
        seconds: this.querySelector('[data-seconds]')
      };
      const tick = () => {
        let diff = Math.max(0, end - Date.now());
        if (diff === 0 && this.dataset.hideOnEnd === 'true') {
          this.closest('.shopify-section')?.style.setProperty('display', 'none');
          clearInterval(this.timer);
          return;
        }
        const d = Math.floor(diff / 86400000); diff -= d * 86400000;
        const h = Math.floor(diff / 3600000); diff -= h * 3600000;
        const m = Math.floor(diff / 60000); diff -= m * 60000;
        const s = Math.floor(diff / 1000);
        if (this.units.days) this.units.days.textContent = String(d).padStart(2, '0');
        if (this.units.hours) this.units.hours.textContent = String(h).padStart(2, '0');
        if (this.units.minutes) this.units.minutes.textContent = String(m).padStart(2, '0');
        if (this.units.seconds) this.units.seconds.textContent = String(s).padStart(2, '0');
      };
      tick();
      this.timer = setInterval(tick, 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
  }
  customElements.define('countdown-timer', CountdownTimer);

  /* ---------------------------------------------------------- before/after */
  class BeforeAfter extends HTMLElement {
    connectedCallback() {
      this.wrap = this.querySelector('.before-after');
      if (!this.wrap) return;
      const move = (clientX) => {
        const rect = this.wrap.getBoundingClientRect();
        const pos = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
        this.wrap.style.setProperty('--ba-pos', `${pos}%`);
      };
      let dragging = false;
      this.wrap.addEventListener('pointerdown', (e) => { dragging = true; this.wrap.setPointerCapture(e.pointerId); move(e.clientX); });
      this.wrap.addEventListener('pointermove', (e) => { if (dragging) move(e.clientX); });
      this.wrap.addEventListener('pointerup', () => { dragging = false; });
    }
  }
  customElements.define('before-after', BeforeAfter);

  /* ----------------------------------------------------- quantity breaks */
  class QtyBreaks extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (e) => {
        const input = e.target.closest('input[name="qty-break"]');
        if (!input) return;
        this.querySelectorAll('.qty-break').forEach((el) => el.classList.toggle('is-selected', el.contains(input)));
        const qtyInput = document.querySelector('product-form form [name="quantity"], [data-main-quantity] input');
        if (qtyInput) {
          qtyInput.value = input.value;
          qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
  }
  customElements.define('qty-breaks', QtyBreaks);

  /* -------------------------------------------------------- recently viewed */
  const RV_KEY = 'alverra:recently-viewed';
  const RecentlyViewed = {
    track(handle) {
      try {
        const list = JSON.parse(sessionStorage.getItem(RV_KEY) || '[]').filter((h) => h !== handle);
        list.unshift(handle);
        sessionStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, 12)));
      } catch (e) { /* storage unavailable */ }
    },
    get() {
      try { return JSON.parse(sessionStorage.getItem(RV_KEY) || '[]'); } catch (e) { return []; }
    }
  };
  window.AlverraXRecentlyViewed = RecentlyViewed;

  class RecentlyViewedProducts extends HTMLElement {
    async connectedCallback() {
      const exclude = this.dataset.exclude;
      const handles = RecentlyViewed.get().filter((h) => h !== exclude).slice(0, parseInt(this.dataset.limit || 4, 10));
      if (!handles.length) { this.closest('.shopify-section')?.style.setProperty('display', 'none'); return; }
      const grid = this.querySelector('[data-rv-grid]');
      if (!grid) return;
      const cards = await Promise.all(handles.map(async (handle) => {
        try {
          const res = await fetch(`${window.AlverraX.routes.root_url === '/' ? '' : window.AlverraX.routes.root_url}/products/${handle}?section_id=product-card-standalone`);
          if (!res.ok) return '';
          return await res.text();
        } catch (e) { return ''; }
      }));
      const parser = new DOMParser();
      const htmlCards = cards.map((c) => parser.parseFromString(c, 'text/html').querySelector('.product-card')).filter(Boolean);
      if (!htmlCards.length) { this.closest('.shopify-section')?.style.setProperty('display', 'none'); return; }
      grid.innerHTML = '';
      htmlCards.forEach((c) => grid.appendChild(c));
    }
  }
  customElements.define('recently-viewed-products', RecentlyViewedProducts);

  /* -------------------------------------------------------------- boot */
  document.addEventListener('DOMContentLoaded', () => {
    initReveal();
    initHeader();
    initMarquees();
    if (window.AlverraX?.productHandle) RecentlyViewed.track(window.AlverraX.productHandle);
  });
})();
